'use client'

import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import {
  FACE_Z,
  FLOATS_PER_VERTEX,
  Geo,
  applyPoint,
  box,
  chain,
  cylinder,
  extrude,
  fanRotor,
  gearOutline,
  mix3,
  multiply,
  perspective,
  ring,
  rotateX,
  rotateY,
  rotateZ,
  scale3,
  sphere,
  translate,
  type M4,
  type V3,
} from './gl-math'

/**
 * The homepage lab machine: a hand-rolled WebGL scene (no 3D library) of an
 * experimental rig — enamel tower, open GPU rack, fans, gears, gauges and
 * glowing tubes. It assembles itself on load, powers on, follows the cursor,
 * can be dragged sideways, and pulses when clicked. Every colour is read from
 * the theme tokens at runtime. Purely decorative: it drives nothing.
 */

export interface LabMachineProps {
  power: boolean
  /** 0..1, from the fan knob. */
  fanSpeed: number
  /** 0..1, how big the selected GPU is relative to the largest. */
  load: number
  /** Bumped by the console when a control changes; flashes the rack. */
  pulse: number
  className?: string
}

const VERT = `
attribute vec3 aPos;
attribute vec3 aNor;
attribute vec2 aGlow;
uniform mat4 uProj;
uniform mat4 uView;
uniform mat4 uModel;
varying vec3 vWorld;
varying vec3 vNormal;
varying vec2 vGlow;
varying vec3 vLocal;
void main() {
  vec4 w = uModel * vec4(aPos, 1.0);
  vWorld = w.xyz;
  vNormal = (uModel * vec4(aNor, 0.0)).xyz;
  vGlow = aGlow;
  vLocal = aPos;
  gl_Position = uProj * uView * w;
}
`

const FRAG = `
#ifdef GL_FRAGMENT_PRECISION_HIGH
precision highp float;
#else
precision mediump float;
#endif
varying vec3 vWorld;
varying vec3 vNormal;
varying vec2 vGlow;
varying vec3 vLocal;
uniform vec3 uColor;
uniform float uMetal;
uniform float uRough;
uniform vec3 uEmissive;
uniform float uEmisScale;
uniform float uFloor;
uniform float uFloorAlpha;
uniform vec3 uCam;
uniform float uTime;
uniform vec3 uKeyDir;
uniform vec3 uKeyCol;
uniform vec3 uFillDir;
uniform vec3 uFillCol;
uniform vec3 uRimDir;
uniform vec3 uRimCol;
uniform vec3 uPointPos;
uniform vec3 uPointCol;
uniform vec3 uCorePos;
uniform vec3 uCoreCol;
uniform vec3 uSky;
uniform vec3 uGround;

vec3 shade(vec3 L, vec3 col, vec3 N, vec3 V, vec3 diff, vec3 F0, float rough) {
  float ndl = max(dot(N, L), 0.0);
  vec3 H = normalize(L + V);
  float sh = mix(220.0, 8.0, rough);
  float sp = pow(max(dot(N, H), 0.0), sh) * (sh + 2.0) / 8.0;
  return col * ndl * (diff + F0 * sp);
}

vec3 envMap(vec3 R, float rough) {
  vec3 c = mix(uGround, uSky, smoothstep(-0.35, 0.65, R.y));
  float strip = smoothstep(0.1 + rough * 0.35, 0.0, abs(R.y - 0.22)) * smoothstep(-0.2, 0.5, R.z);
  c += uKeyCol * 0.32 * strip;
  float soft = pow(max(dot(R, normalize(vec3(-0.45, 0.7, 0.55))), 0.0), mix(70.0, 5.0, rough));
  c += uKeyCol * 0.55 * soft;
  return c;
}

void main() {
  if (uFloor > 0.5) {
    float d = length(vLocal.xz / vec2(4.3, 2.3));
    float a = (1.0 - smoothstep(0.2, 1.0, d)) * 0.62 * uFloorAlpha;
    gl_FragColor = vec4(0.0, 0.0, 0.0, a);
    return;
  }
  vec3 N = normalize(vNormal);
  vec3 V = normalize(uCam - vWorld);
  vec3 F0 = mix(vec3(0.04), uColor, uMetal);
  vec3 diff = uColor * (1.0 - uMetal);
  vec3 c = vec3(0.0);
  c += shade(normalize(uKeyDir), uKeyCol, N, V, diff, F0, uRough);
  c += shade(normalize(uFillDir), uFillCol, N, V, diff, F0, uRough);
  c += shade(normalize(uRimDir), uRimCol, N, V, diff, F0, uRough);
  vec3 lp = uPointPos - vWorld;
  float dp = length(lp);
  c += shade(lp / dp, uPointCol / (1.0 + 0.05 * dp * dp), N, V, diff, F0, uRough);
  vec3 lc = uCorePos - vWorld;
  float dc = length(lc);
  c += shade(lc / dc, uCoreCol / (1.0 + 0.6 * dc * dc), N, V, diff, F0, uRough);
  float ndv = max(dot(N, V), 0.0);
  vec3 F = F0 + (1.0 - F0) * pow(1.0 - ndv, 5.0);
  c += diff * mix(uGround, uSky, N.y * 0.5 + 0.5) * 0.5;
  c += envMap(reflect(-V, N), uRough) * F * (1.0 - uRough * 0.55);
  float seed = vGlow.y;
  float blink = seed > 0.0 ? 0.2 + 0.8 * step(0.42, fract(uTime * (0.3 + seed * 0.9) + seed * 13.0)) : 1.0;
  c += uEmissive * vGlow.x * blink * uEmisScale;
  c = max(c, vec3(0.0));
  c = clamp((c * (2.51 * c + 0.03)) / (c * (2.43 * c + 0.59) + 0.14), 0.0, 1.0);
  gl_FragColor = vec4(pow(c, vec3(1.0 / 2.2)), 1.0);
}
`

type Channel = 'none' | 'tube' | 'led' | 'card' | 'needle'

interface Material {
  color: V3
  metal: number
  rough: number
  emissive: V3
  channel: Channel
  floor?: boolean
}

interface Sim {
  fan: number
  gear: number
  needles: [number, number]
}

interface Part {
  buffer: WebGLBuffer
  count: number
  mat: Material
  pos: V3
  base?: M4
  from: V3
  delay: number
  spin?: (s: Sim) => number
}

interface Palette {
  bg: V3
  fg: V3
  primary: V3
  accent: V3
  secondary: V3
  muted: V3
}

const ASSEMBLE_SECONDS = 0.85
const POWER_ON_AT = 2.4
const FOV = (30 * Math.PI) / 180

const toLinear = (v: number) => Math.pow(v / 255, 2.2)
const clamp = (v: number, lo = 0, hi = 1) => Math.min(hi, Math.max(lo, v))
const easeOutBack = (x: number) => {
  const c1 = 1.35
  const c3 = c1 + 1
  return 1 + c3 * Math.pow(x - 1, 3) + c1 * Math.pow(x - 1, 2)
}

/** Resolve the theme tokens to linear RGB by painting them onto a 1px canvas. */
function readPalette(el: Element): Palette {
  const cs = getComputedStyle(el)
  const probe = document.createElement('canvas')
  probe.width = 1
  probe.height = 1
  const ctx = probe.getContext('2d', { willReadFrequently: true })
  const read = (name: string, fallback: number): V3 => {
    const raw = cs.getPropertyValue(name).trim()
    if (!ctx || !raw) return [fallback, fallback, fallback]
    ctx.clearRect(0, 0, 1, 1)
    ctx.fillStyle = raw
    ctx.fillRect(0, 0, 1, 1)
    const d = ctx.getImageData(0, 0, 1, 1).data
    return [toLinear(d[0]), toLinear(d[1]), toLinear(d[2])]
  }
  return {
    bg: read('--background', 0.8),
    fg: read('--foreground', 0.02),
    primary: read('--primary', 0.3),
    accent: read('--accent', 0.4),
    secondary: read('--secondary', 0.75),
    muted: read('--muted-foreground', 0.12),
  }
}

function materials(P: Palette): Record<string, Material> {
  const none: V3 = [0, 0, 0]
  const m = (color: V3, metal: number, rough: number, emissive: V3 = none, channel: Channel = 'none'): Material => ({
    color,
    metal,
    rough,
    emissive,
    channel,
  })
  return {
    floor: { ...m(none, 0, 1), floor: true },
    enamel: m(mix3(P.secondary, P.bg, 0.3), 0, 0.5),
    faceplate: m(mix3(P.fg, P.muted, 0.35), 0.55, 0.42),
    steel: m(mix3(P.muted, P.bg, 0.55), 1, 0.26),
    darkSteel: m(mix3(P.fg, P.muted, 0.6), 0.9, 0.38),
    paint: m(P.primary, 0.05, 0.36),
    copper: m(mix3(P.primary, P.accent, 0.55), 1, 0.2),
    brass: m(mix3(P.accent, P.bg, 0.45), 1, 0.28),
    rubber: m(scale3(P.fg, 0.8), 0, 0.9),
    gaugeFace: m(P.bg, 0, 0.32),
    tube: m(mix3(P.accent, P.bg, 0.35), 0, 0.2, scale3(P.accent, 3.4), 'tube'),
    led: m(mix3(P.fg, P.muted, 0.25), 0.3, 0.4, scale3(P.accent, 2.8), 'led'),
    card: m(mix3(P.fg, P.muted, 0.3), 0.7, 0.35, scale3(P.accent, 2.6), 'card'),
    needle: m(P.primary, 0, 0.4, scale3(P.accent, 0.9), 'needle'),
  }
}

function buildScene(gl: WebGLRenderingContext, P: Palette): Part[] {
  const M = materials(P)
  const parts: Part[] = []
  const add = (
    geo: Geo,
    mat: Material,
    pos: V3,
    opts: { from?: V3; delay?: number; base?: M4; spin?: (s: Sim) => number } = {}
  ) => {
    const buffer = gl.createBuffer()
    if (!buffer) return
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer)
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(geo.data), gl.STATIC_DRAW)
    parts.push({
      buffer,
      count: geo.count,
      mat,
      pos,
      base: opts.base,
      from: opts.from ?? [0, 0, 0],
      delay: opts.delay ?? 0,
      spin: opts.spin,
    })
  }
  const along = (geo: Geo, m: M4) => new Geo().add(geo, m)

  // Contact shadow
  const floor = new Geo()
  floor.quad([-5, 0, 4], [5, 0, 4], [5, 0, -4], [-5, 0, -4], [0, 1, 0])
  add(floor, M.floor, [0, 0.005, 0], { delay: -1 })

  // Plinth
  const rise = { delay: 0, from: [0, -1.6, 0] as V3 }
  add(box(6.6, 0.5, 3.4), M.darkSteel, [0, 0.35, 0], rise)
  const feet = new Geo()
  for (const x of [-3, 3]) for (const z of [-1.4, 1.4]) feet.add(cylinder(0.24, 0.14, 16), translate(x, 0.07, z))
  add(feet, M.rubber, [0, 0, 0], rise)
  add(box(6.7, 0.1, 3.5), M.paint, [0, 0.63, 0], rise)
  const rivets = new Geo()
  for (let i = 0; i < 13; i++) rivets.add(sphere(0.05, 8, 6), translate(-3 + i * 0.5, 0.35, 1.71))
  add(rivets, M.steel, [0, 0, 0], rise)

  // Enamel tower with a dark faceplate
  const drop = { delay: 0.25, from: [0, 6, 0] as V3 }
  add(box(2.5, 3.5, 2.2), M.enamel, [-1.25, 2.43, -0.1], drop)
  const corners = new Geo()
    .add(box(0.1, 3.5, 0.1), translate(-2.5, 0, 1.0))
    .add(box(0.1, 3.5, 0.1), translate(0, 0, 1.0))
    .add(box(2.6, 0.1, 0.1), translate(-1.25, 1.75, 1.0))
  add(corners, M.paint, [0, 2.43, 0], drop)
  add(box(2.2, 3.2, 0.08), M.faceplate, [-1.25, 2.43, 1.04], { delay: 0.5, from: [0, 0, 3] })
  const vents = new Geo()
  for (let i = 0; i < 7; i++) vents.add(box(0.05, 0.07, 1.5), translate(0.025, 3.7 - i * 0.16, -0.1))
  add(vents, M.darkSteel, [0, 0, 0], { delay: 0.55, from: [1.5, 0, 0] })

  // Main fan
  const fanIn = { delay: 0.7, from: [0, 0, 2.5] as V3 }
  add(along(cylinder(0.62, 0.04, 36), FACE_Z), M.rubber, [-1.25, 3.2, 1.1], fanIn)
  const housing = new Geo()
    .add(ring(0.62, 0.76, 0.18, 40), FACE_Z)
    .add(box(1.24, 0.04, 0.03), translate(0, 0, 0.1))
    .add(box(0.04, 1.24, 0.03), translate(0, 0, 0.1))
  add(housing, M.steel, [-1.25, 3.2, 1.17], fanIn)
  add(fanRotor(0.58), M.steel, [-1.25, 3.2, 1.15], { delay: 0.8, from: [0, 0, 3], spin: (s) => s.fan })

  // Two analog gauges
  const gauge = (cx: number, which: 0 | 1, delay: number) => {
    const g = { delay, from: [0, 0, 2.5] as V3 }
    add(along(ring(0.34, 0.43, 0.14, 36), FACE_Z), M.brass, [cx, 1.85, 1.13], g)
    add(along(cylinder(0.34, 0.03, 36), FACE_Z), M.gaugeFace, [cx, 1.85, 1.1], g)
    const ticks = new Geo()
    for (let i = 0; i <= 10; i++) {
      ticks.add(box(0.018, i % 5 === 0 ? 0.08 : 0.05, 0.01), chain(rotateZ(2.2 - i * 0.44), translate(0, 0.27, 0)))
    }
    ticks.add(cylinder(0.045, 0.04, 14), chain(translate(0, 0, 0.03), FACE_Z))
    add(ticks, M.darkSteel, [cx, 1.85, 1.125], g)
    add(along(box(0.025, 0.3, 0.015), translate(0, 0.12, 0)), M.needle, [cx, 1.85, 1.14], {
      delay: delay + 0.15,
      from: [0, 0, 2.5],
      spin: (s) => s.needles[which],
    })
  }
  gauge(-1.8, 0, 0.9)
  gauge(-0.7, 1, 0.98)

  // Illuminated push buttons
  const buttons = new Geo()
  for (let i = 0; i < 7; i++) {
    const x = -2.15 + i * 0.3
    buttons.add(box(0.22, 0.16, 0.08), translate(x, 1.07, 1.12))
    buttons.add(box(0.15, 0.09, 0.05).glow(1, 0.13 + i * 0.11), translate(x, 1.07, 1.175))
  }
  add(buttons, M.led, [0, 0, 0], { delay: 1.3, from: [0, 0, 1.5] })

  // Open GPU rack
  const rack = new Geo()
  for (const x of [0.4, 2.9]) for (const z of [-0.95, 0.95]) rack.add(cylinder(0.06, 2.7, 12), translate(x, 2.03, z))
  rack.add(box(2.7, 0.1, 2.1), translate(1.65, 3.4, 0))
  rack.add(box(2.6, 0.06, 2.0), translate(1.65, 0.74, 0))
  add(rack, M.steel, [0, 0, 0], { delay: 0.4, from: [0, 6, 0] })
  for (let i = 0; i < 4; i++) {
    const card = new Geo().add(box(2.3, 0.26, 1.7))
    for (let f = 0; f < 9; f++) card.add(box(0.03, 0.07, 1.4), translate(-1.0 + f * 0.25, 0.165, -0.05))
    card.add(box(2.3, 0.07, 0.04).glow(1, 0.21 + i * 0.17), translate(0, 0, 0.87))
    add(card, M.card, [1.65, 1.1 + i * 0.52, -0.05], { delay: 0.95 + i * 0.13, from: [0, 0, 4] })
  }

  // Side exhaust fan on the rack
  const sideFan = rotateY(Math.PI / 2)
  const sideHousing = new Geo()
    .add(ring(0.5, 0.6, 0.16, 36), FACE_Z)
    .add(box(1.0, 0.035, 0.03), translate(0, 0, 0.09))
    .add(box(0.035, 1.0, 0.03), translate(0, 0, 0.09))
  add(sideHousing, M.steel, [3.02, 2.0, 0], { delay: 1.0, from: [2.5, 0, 0], base: sideFan })
  add(fanRotor(0.47, 6), M.darkSteel, [3.0, 2.0, 0], {
    delay: 1.1,
    from: [3, 0, 0],
    base: sideFan,
    spin: (s) => -s.fan * 1.3,
  })

  // Gearbox
  add(box(2.6, 1.5, 0.12), M.paint, [1.65, 4.18, -0.55], { delay: 1.2, from: [0, 5, 0] })
  const gearIn = (delay: number) => ({ delay, from: [0, 0, 2.5] as V3 })
  add(along(extrude(gearOutline(14, 0.55, 0.66), 0.12), FACE_Z), M.brass, [1.15, 4.25, -0.42], {
    ...gearIn(1.35),
    spin: (s) => s.gear,
  })
  add(along(extrude(gearOutline(10, 0.37, 0.48), 0.12), FACE_Z), M.copper, [2.12, 3.9, -0.42], {
    ...gearIn(1.45),
    spin: (s) => -s.gear * 1.4 + Math.PI / 10,
  })
  add(along(extrude(gearOutline(8, 0.29, 0.4), 0.12), FACE_Z), M.brass, [2.615, 4.49, -0.42], {
    ...gearIn(1.55),
    spin: (s) => s.gear * 1.75,
  })
  const hubs = new Geo()
  for (const [x, y] of [
    [1.15, 4.25],
    [2.12, 3.9],
    [2.615, 4.49],
  ]) {
    hubs.add(cylinder(0.11, 0.22, 16), chain(translate(x, y, -0.36), FACE_Z))
  }
  add(hubs, M.darkSteel, [0, 0, 0], { delay: 1.6, from: [0, 0, 2] })

  // Valve tubes on top of the tower
  const sockets = new Geo()
  const filaments = new Geo()
  for (const x of [-2.05, -1.45, -0.85]) {
    sockets.add(cylinder(0.22, 0.2, 20), translate(x, 4.28, -0.35))
    filaments.add(cylinder(0.14, 0.62, 20), translate(x, 4.69, -0.35))
    filaments.add(sphere(0.14, 16, 8), translate(x, 5.0, -0.35))
  }
  add(sockets, M.darkSteel, [0, 0, 0], { delay: 1.5, from: [0, 3, 0] })
  add(filaments.glow(1, 0), M.tube, [0, 0, 0], { delay: 1.6, from: [0, 3.5, 0] })

  // Copper plumbing
  const pipes = new Geo()
    .add(cylinder(0.08, 0.5, 16), translate(-0.3, 4.43, -0.55))
    .add(sphere(0.11, 12, 8), translate(-0.3, 4.68, -0.55))
    .add(cylinder(0.08, 0.65, 16), chain(translate(0.025, 4.68, -0.55), rotateZ(Math.PI / 2)))
    .add(cylinder(0.14, 0.08, 16), chain(translate(0.35, 4.68, -0.55), rotateZ(Math.PI / 2)))
    .add(cylinder(0.08, 5.9, 20), chain(translate(0.05, 0.78, 1.45), rotateZ(Math.PI / 2)))
    .add(sphere(0.11, 12, 8), translate(-2.9, 0.78, 1.45))
    .add(sphere(0.11, 12, 8), translate(3.0, 0.78, 1.45))
  add(pipes, M.copper, [0, 0, 0], { delay: 1.7, from: [0, 3, 0] })

  // Antenna with a beacon
  const mast = new Geo()
    .add(cylinder(0.03, 0.75, 10), translate(0, 0.375, 0))
    .add(sphere(0.1, 12, 8).glow(1, 0.77), translate(0, 0.82, 0))
  add(mast, M.led, [-0.2, 4.18, 0.7], { delay: 1.8, from: [0, 2.5, 0] })

  return parts
}

function compile(gl: WebGLRenderingContext): WebGLProgram | null {
  const shader = (type: number, src: string) => {
    const s = gl.createShader(type)
    if (!s) return null
    gl.shaderSource(s, src)
    gl.compileShader(s)
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
      gl.deleteShader(s)
      return null
    }
    return s
  }
  const vs = shader(gl.VERTEX_SHADER, VERT)
  const fs = shader(gl.FRAGMENT_SHADER, FRAG)
  if (!vs || !fs) return null
  const prog = gl.createProgram()
  if (!prog) return null
  gl.attachShader(prog, vs)
  gl.attachShader(prog, fs)
  gl.linkProgram(prog)
  gl.deleteShader(vs)
  gl.deleteShader(fs)
  if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
    gl.deleteProgram(prog)
    return null
  }
  return prog
}

export function LabMachine({ power, fanSpeed, load, pulse, className }: LabMachineProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const propsRef = useRef({ power, fanSpeed, load, pulse })
  const redrawRef = useRef<() => void>(() => {})
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    propsRef.current = { power, fanSpeed, load, pulse }
    redrawRef.current()
  }, [power, fanSpeed, load, pulse])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const gl = canvas.getContext('webgl', { antialias: true, alpha: true, powerPreference: 'high-performance' })
    if (!gl) {
      setFailed(true)
      return
    }
    const prog = compile(gl)
    if (!prog) {
      setFailed(true)
      return
    }
    const P = readPalette(canvas)
    const parts = buildScene(gl, P)
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches

    gl.useProgram(prog)
    const aPos = gl.getAttribLocation(prog, 'aPos')
    const aNor = gl.getAttribLocation(prog, 'aNor')
    const aGlow = gl.getAttribLocation(prog, 'aGlow')
    gl.enableVertexAttribArray(aPos)
    gl.enableVertexAttribArray(aNor)
    gl.enableVertexAttribArray(aGlow)
    const names = [
      'uProj', 'uView', 'uModel', 'uColor', 'uMetal', 'uRough', 'uEmissive', 'uEmisScale', 'uFloor',
      'uFloorAlpha', 'uCam', 'uTime', 'uKeyDir', 'uKeyCol', 'uFillDir', 'uFillCol', 'uRimDir', 'uRimCol',
      'uPointPos', 'uPointCol', 'uCorePos', 'uCoreCol', 'uSky', 'uGround',
    ] as const
    const U = Object.fromEntries(names.map((n) => [n, gl.getUniformLocation(prog, n)])) as Record<
      (typeof names)[number],
      WebGLUniformLocation | null
    >

    gl.enable(gl.DEPTH_TEST)
    gl.enable(gl.BLEND)
    gl.blendFuncSeparate(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA, gl.ONE, gl.ONE_MINUS_SRC_ALPHA)
    gl.clearColor(0, 0, 0, 0)

    // Constant lighting, derived from the palette
    gl.uniform3fv(U.uKeyDir, [-0.5, 0.9, 0.75])
    gl.uniform3fv(U.uKeyCol, scale3(P.bg, 2.3))
    gl.uniform3fv(U.uFillDir, [0.9, 0.25, 0.35])
    gl.uniform3fv(U.uFillCol, scale3(mix3(P.muted, P.bg, 0.4), 0.7))
    gl.uniform3fv(U.uRimDir, [0.3, 0.45, -1])
    gl.uniform3fv(U.uRimCol, scale3(P.accent, 1.3))
    gl.uniform3fv(U.uSky, scale3(P.bg, 0.85))
    gl.uniform3fv(U.uGround, scale3(P.fg, 0.6))

    const sim: Sim = { fan: 0, gear: 0, needles: [2.2, 2.2] }
    const needleVel = [0, 0]
    let fanVel = 0
    let gearVel = 0
    let powerLevel = reduced ? (propsRef.current.power ? 1 : 0) : 0
    let pulseLevel = 0
    let lastPulse = propsRef.current.pulse
    let t = reduced ? 10 : 0
    const pointer = { x: 0, y: 0, tx: 0, ty: 0, hover: 0.35 }
    let drag = 0
    let dragging: { x: number; start: number; moved: boolean } | null = null
    let scroll = 0
    let width = 1
    let height = 1

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 1.75)
      width = Math.max(1, Math.round(canvas.clientWidth * dpr))
      height = Math.max(1, Math.round(canvas.clientHeight * dpr))
      canvas.width = width
      canvas.height = height
      gl.viewport(0, 0, width, height)
    }

    const simulate = (dt: number) => {
      const p = propsRef.current
      if (p.pulse !== lastPulse) {
        lastPulse = p.pulse
        pulseLevel = 1
      }
      const target = (t > POWER_ON_AT || reduced) && p.power ? 1 : 0
      const needleTargets: [number, number] = [
        2.2 - 4.4 * clamp(powerLevel * (0.12 + p.fanSpeed * 0.8 + Math.sin(t * 5.3) * 0.015)),
        2.2 - 4.4 * clamp(powerLevel * (0.1 + p.load * 0.82 + pulseLevel * 0.12 + Math.sin(t * 3.1) * 0.012)),
      ]
      if (reduced) {
        powerLevel = target
        pulseLevel = 0
        sim.needles = [
          2.2 - 4.4 * clamp(powerLevel * (0.12 + p.fanSpeed * 0.8)),
          2.2 - 4.4 * clamp(powerLevel * (0.1 + p.load * 0.82)),
        ]
        sim.fan = 0.3
        sim.gear = 0
        return
      }
      powerLevel += (target - powerLevel) * Math.min(1, dt * 2.2)
      pulseLevel *= Math.exp(-dt * 2.2)
      const fanTarget = powerLevel * (0.8 + p.fanSpeed * 11) + pulseLevel * 8
      fanVel += (fanTarget - fanVel) * Math.min(1, dt * 1.3)
      sim.fan += fanVel * dt
      const gearTarget = powerLevel * (0.2 + p.fanSpeed * 0.9) + pulseLevel * 0.9
      gearVel += (gearTarget - gearVel) * Math.min(1, dt * 2)
      sim.gear += gearVel * dt
      for (const i of [0, 1] as const) {
        needleVel[i] += (needleTargets[i] - sim.needles[i]) * 55 * dt
        needleVel[i] *= Math.exp(-7 * dt)
        sim.needles[i] += needleVel[i] * dt
      }
      pointer.x += (pointer.tx - pointer.x) * Math.min(1, dt * 4)
      pointer.y += (pointer.ty - pointer.y) * Math.min(1, dt * 4)
      if (!dragging) drag *= Math.exp(-dt * 1.6)
    }

    const draw = () => {
      const aspect = width / height
      const tanHalf = Math.tan(FOV / 2)
      const dist = Math.max(3.15 / tanHalf, 4.05 / (tanHalf * aspect))
      const sway = reduced ? 0 : Math.sin(t * 0.35) * 0.06
      const yaw = -0.52 + pointer.x * 0.28 + drag + sway
      const pitch = 0.2 - pointer.y * 0.09 + scroll * 0.22
      const target: V3 = [0.25, 2.55 - scroll * 0.5, 0]
      const view = chain(translate(0, 0, -dist), rotateX(pitch), rotateY(yaw), translate(-target[0], -target[1], -target[2]))
      const inverse = chain(translate(target[0], target[1], target[2]), rotateY(-yaw), rotateX(-pitch))
      const cam = applyPoint(inverse, [0, 0, dist])
      const cursorLight = applyPoint(inverse, [pointer.x * 3.6, -pointer.y * 2.6 + 0.6, dist * 0.45])

      const flicker =
        powerLevel > 0.02 && powerLevel < 0.97 ? (Math.sin(t * 67) * Math.sin(t * 23) > -0.25 ? 1 : 0.25) : 1
      const tube = powerLevel * flicker * (0.9 + 0.1 * Math.sin(t * 2.3))
      const scales: Record<Channel, number> = {
        none: 0,
        tube,
        led: powerLevel,
        card: powerLevel * (0.7 + pulseLevel * 2),
        needle: powerLevel * 0.5,
      }

      gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT)
      gl.uniformMatrix4fv(U.uProj, false, perspective(FOV, aspect, 0.5, 80))
      gl.uniformMatrix4fv(U.uView, false, view)
      gl.uniform3fv(U.uCam, cam)
      gl.uniform1f(U.uTime, reduced ? 0 : t)
      gl.uniform3fv(U.uPointPos, cursorLight)
      gl.uniform3fv(U.uPointCol, scale3(P.bg, 1.6 * pointer.hover))
      gl.uniform3fv(U.uCorePos, [-1.45, 4.9, 0.25])
      gl.uniform3fv(U.uCoreCol, scale3(P.accent, 7 * tube))

      for (const part of parts) {
        const local = (t - part.delay) / ASSEMBLE_SECONDS
        if (part.delay >= 0 && local <= 0) continue
        const e = local >= 1 ? 1 : easeOutBack(clamp(local))
        let model = translate(
          part.pos[0] + part.from[0] * (1 - e),
          part.pos[1] + part.from[1] * (1 - e),
          part.pos[2] + part.from[2] * (1 - e)
        )
        if (part.base) model = multiply(model, part.base)
        if (part.spin) model = multiply(model, rotateZ(part.spin(sim)))
        const mat = part.mat
        gl.uniformMatrix4fv(U.uModel, false, model)
        gl.uniform3fv(U.uColor, mat.color)
        gl.uniform1f(U.uMetal, mat.metal)
        gl.uniform1f(U.uRough, mat.rough)
        gl.uniform3fv(U.uEmissive, mat.emissive)
        gl.uniform1f(U.uEmisScale, scales[mat.channel])
        gl.uniform1f(U.uFloor, mat.floor ? 1 : 0)
        gl.uniform1f(U.uFloorAlpha, clamp(t / 1.2))
        gl.depthMask(!mat.floor)
        gl.bindBuffer(gl.ARRAY_BUFFER, part.buffer)
        const stride = FLOATS_PER_VERTEX * 4
        gl.vertexAttribPointer(aPos, 3, gl.FLOAT, false, stride, 0)
        gl.vertexAttribPointer(aNor, 3, gl.FLOAT, false, stride, 12)
        gl.vertexAttribPointer(aGlow, 2, gl.FLOAT, false, stride, 24)
        gl.drawArrays(gl.TRIANGLES, 0, part.count)
      }
      gl.depthMask(true)
    }

    // Loop: continuous unless reduced motion; paused while off screen
    let raf = 0
    let running = false
    let visible = true
    let last = 0
    const frame = (now: number) => {
      const dt = last ? Math.min((now - last) / 1000, 0.05) : 0.016
      last = now
      t += dt
      simulate(dt)
      draw()
      if (visible && !reduced) raf = requestAnimationFrame(frame)
      else running = false
    }
    const start = () => {
      if (running || reduced) return
      running = true
      last = 0
      raf = requestAnimationFrame(frame)
    }
    const drawOnce = () => {
      cancelAnimationFrame(raf)
      raf = requestAnimationFrame(() => {
        simulate(0)
        draw()
      })
    }
    redrawRef.current = reduced ? drawOnce : start

    resize()
    if (reduced) drawOnce()
    else start()

    const ro = new ResizeObserver(() => {
      resize()
      if (reduced) drawOnce()
    })
    ro.observe(canvas)
    const io = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting
      if (visible) start()
    })
    io.observe(canvas)

    const onPointerMove = (e: PointerEvent) => {
      const r = canvas.getBoundingClientRect()
      pointer.tx = clamp((e.clientX - (r.left + r.width / 2)) / (r.width / 2), -1, 1)
      pointer.ty = clamp((e.clientY - (r.top + r.height / 2)) / (r.height / 2), -1, 1)
      const inside = e.clientX > r.left && e.clientX < r.right && e.clientY > r.top && e.clientY < r.bottom
      pointer.hover = inside ? 1 : 0.35
      if (dragging) {
        const dx = e.clientX - dragging.x
        if (Math.abs(dx) > 5) dragging.moved = true
        drag = clamp(dragging.start + dx * 0.006, -1.1, 1.1)
      }
    }
    const onPointerDown = (e: PointerEvent) => {
      dragging = { x: e.clientX, start: drag, moved: false }
      canvas.setPointerCapture(e.pointerId)
    }
    const onPointerUp = () => {
      if (dragging && !dragging.moved && !reduced) pulseLevel = 1
      dragging = null
    }
    const onScroll = () => {
      const r = canvas.getBoundingClientRect()
      scroll = reduced ? 0 : clamp(-r.top / Math.max(r.height, 1))
    }
    window.addEventListener('pointermove', onPointerMove, { passive: true })
    canvas.addEventListener('pointerdown', onPointerDown)
    canvas.addEventListener('pointerup', onPointerUp)
    canvas.addEventListener('pointercancel', onPointerUp)
    window.addEventListener('scroll', onScroll, { passive: true })

    const onLost = (e: Event) => {
      e.preventDefault()
      cancelAnimationFrame(raf)
      setFailed(true)
    }
    canvas.addEventListener('webglcontextlost', onLost)

    return () => {
      cancelAnimationFrame(raf)
      ro.disconnect()
      io.disconnect()
      window.removeEventListener('pointermove', onPointerMove)
      canvas.removeEventListener('pointerdown', onPointerDown)
      canvas.removeEventListener('pointerup', onPointerUp)
      canvas.removeEventListener('pointercancel', onPointerUp)
      window.removeEventListener('scroll', onScroll)
      canvas.removeEventListener('webglcontextlost', onLost)
      redrawRef.current = () => {}
      for (const part of parts) gl.deleteBuffer(part.buffer)
      gl.deleteProgram(prog)
    }
  }, [])

  return (
    <div className={cn('relative', className)}>
      {failed ? (
        <div aria-hidden className="lab-fallback absolute inset-[12%]" />
      ) : (
        <canvas
          ref={canvasRef}
          aria-hidden
          className="absolute inset-0 h-full w-full cursor-grab touch-pan-y active:cursor-grabbing"
        />
      )}
    </div>
  )
}
